import { lazy, Suspense, type ReactNode } from "react";
import { Redirect, Route, Switch, useLocation } from "wouter";
import { AuthProvider, useAuth } from "./hooks/use-auth";
import { RouteBoundary } from "./components/route-boundary";

/**
 * Every route is code-split so the marketing entry never downloads the
 * authenticated app (Radix, the venture surfaces, the Build Kit editor).
 */
const LandingPage = lazy(() => import("./pages/landing/index").then((m) => ({ default: m.LandingPage })));
const AuthPage = lazy(() => import("./pages/auth").then((m) => ({ default: m.AuthPage })));
const DashboardPage = lazy(() => import("./pages/dashboard").then((m) => ({ default: m.DashboardPage })));
const NewProjectPage = lazy(() => import("./pages/dashboard").then((m) => ({ default: m.NewProjectPage })));
const StudioInsightsPage = lazy(() => import("./pages/dashboard").then((m) => ({ default: m.StudioInsightsPage })));
const SettingsPage = lazy(() => import("./pages/settings").then((m) => ({ default: m.SettingsPage })));
const IntakePage = lazy(() => import("./pages/intake/index").then((m) => ({ default: m.IntakePage })));
const OverviewSurface = lazy(() => import("./pages/venture/overview").then((m) => ({ default: m.OverviewSurface })));
const VentureContextSurface = lazy(() => import("./pages/venture/context").then((m) => ({ default: m.VentureContextSurface })));
const ProofReportSurface = lazy(() => import("./pages/venture/proof-report").then((m) => ({ default: m.ProofReportSurface })));
const ProofLabSurface = lazy(() => import("./pages/venture/proof-lab").then((m) => ({ default: m.ProofLabSurface })));
const BuildKitSurface = lazy(() => import("./pages/venture/build-kit").then((m) => ({ default: m.BuildKitSurface })));
const BoardroomSurface = lazy(() => import("./pages/venture/boardroom").then((m) => ({ default: m.BoardroomSurface })));
const LibrarySurface = lazy(() => import("./pages/venture/library").then((m) => ({ default: m.LibrarySurface })));

function Loader({ label = "Loading" }: { label?: string }) {
  return <div className="vp-loader"><span className="vp-loader-mark" /><p>{label}</p></div>;
}

function ProtectedPage({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const [location] = useLocation();
  if (loading) return <Loader label="Auth · checking" />;
  if (!user) return <Redirect to={`/login?from=${encodeURIComponent(location)}`} replace />;
  return children;
}

function Protected({ children }: { children: ReactNode }) {
  return <ProtectedPage><Suspense fallback={<Loader />}>{children}</Suspense></ProtectedPage>;
}

export function App() {
  return (
    <AuthProvider>
      <RouteBoundary>
        <Suspense fallback={<Loader />}>
          <Switch>
            <Route path="/"><LandingPage /></Route>
            <Route path="/login"><AuthPage mode="login" /></Route>
            <Route path="/register"><AuthPage mode="register" /></Route>
            <Route path="/app"><Protected><DashboardPage /></Protected></Route>
            <Route path="/app/new"><Protected><NewProjectPage /></Protected></Route>
            <Route path="/app/insights"><Protected><StudioInsightsPage /></Protected></Route>
            <Route path="/app/settings"><Protected><SettingsPage /></Protected></Route>
            <Route path="/app/projects/:projectId/intake"><Protected><IntakePage /></Protected></Route>
            <Route path="/app/projects/:projectId/overview"><Protected><OverviewSurface /></Protected></Route>
            <Route path="/app/projects/:projectId/venture-context"><Protected><VentureContextSurface /></Protected></Route>
            <Route path="/app/projects/:projectId/proof-report"><Protected><ProofReportSurface /></Protected></Route>
            <Route path="/app/projects/:projectId/proof-lab"><Protected><ProofLabSurface /></Protected></Route>
            <Route path="/app/projects/:projectId/build-kit"><Protected><BuildKitSurface /></Protected></Route>
            <Route path="/app/projects/:projectId/boardroom"><Protected><BoardroomSurface /></Protected></Route>
            <Route path="/app/projects/:projectId/library"><Protected><LibrarySurface /></Protected></Route>
            <Route path="/app/projects/:projectId"><Protected><OverviewSurface /></Protected></Route>
            <Route><Redirect to="/" replace /></Route>
          </Switch>
        </Suspense>
      </RouteBoundary>
    </AuthProvider>
  );
}
